import {Brand} from '@aboutyou/grpc-clients/tadarida/aysa_api/entities/brands/v1/brand.types';
import {SizePrice} from '@aboutyou/grpc-clients/tadarida/aysa_api/entities/products/v1/price.types';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/entities/products/v1/product.types';
import {Price} from '@aboutyou/grpc-clients/tadarida/aysa_api/features/price/v1/price.types';
import {
  TrackingRecoSectionContext,
  TrackingSectionContext,
  TrackingRecoElement,
  TrackingElementContext,
} from '@aboutyou/legacy-tracking-sdk';

export const trackingProductGetter = ({
  product,
  price,
  brand,
  streamPosition,
  sortChannel,
  pageType,
  recoType,
  headline,
  elements,
  sectionIndex,
}: {
  product: Product;
  price?: Price;
  brand: Brand;
  streamPosition: number;
  sectionIndex?: number;
  sortChannel?: string;
  pageType?: string;
  recoType?: string;
  headline?: string;
  elements?: TrackingRecoElement[];
}) => {
  const section = getSections({
    sortChannel,
    pageType,
    recoType,
    headline,
    elements,
    sectionIndex,
  }).section;

  const element = {
    type: 'product-list-element-content',
    rowIndexInSection: NaN,
    columnIndexInSection: NaN,
    elementIndexInSection: NaN,
    productIndexOnPage: streamPosition,
  } as TrackingElementContext;

  return {
    product: getProduct({product, price, brand}),
    section,
    element,
  };
};

const getProduct = ({
  product,
  price,
  brand,
}: {
  product: Product;
  price?: Price;
  brand: Brand;
}) => {
  const {
    colors,
    id,
    masterKey,
    name,
    price: minPrice,
    isSoldOut,
    path,
    isSponsored,
  } = product;

  return {
    id,
    name,
    brand: {
      id: brand?.id,
      name: brand?.name,
    },
    colors,
    minPrice: getMinPrices(minPrice?.minPrice, isSoldOut),
    price: getPrice(price),
    isSoldOut,
    url: path,
    masterKey,
    isSponsored,
    sponsoredType: isSponsored ? 'sponsored' : undefined,
  };
};

export const getPrice = (price?: Price): Price => {
  if (price) {
    return price;
  }

  return {
    price: {amount: 0, currencyCode: ''},
    hasDifferentPrices: false,
    reductions: [],
    tracker: {
      currencyCode: '',
      price: 0,
      priceWithoutTax: 0,
      fullPrice: 0,
    },
  };
};

const getMinPrices = (price: SizePrice | undefined, isSoldOut: boolean) => {
  if (price === undefined && isSoldOut) {
    return {
      withTax: 0,
      withoutTax: 0,
      campaignReductionWithTax: 0,
      currency: '',
      saleReductionWithTax: 0,
    };
  } else if (price === undefined) {
    throw new Error('productTile.product.price.minPrice is undefined');
  }

  const {currencyCode, tax, withTax, saleReduction, campaignReduction} = price;

  return {
    withTax,
    withoutTax: withTax - tax,
    campaignReductionWithTax: campaignReduction?.reductionAmountWithTax || 0,
    currency: currencyCode,
    saleReductionWithTax: saleReduction?.reductionAmountWithTax || 0,
  };
};

export const getSections = ({
  sortChannel,
  pageType,
  recoType,
  headline,
  elements,
  sectionIndex,
  blockTypeAndId,
  linkedContent,
}: {
  sectionIndex?: number;
  sortChannel?: string;
  pageType?: string;
  recoType?: string;
  headline?: string;
  elements?: TrackingRecoElement[];
  blockTypeAndId?: string;
  linkedContent?: string;
} = {}) => {
  if (
    pageType !== 'adp' &&
    pageType !== 'wishlist' &&
    pageType !== 'brand_discover' &&
    pageType !== 'highlight_page' &&
    pageType !== 'customPage' &&
    pageType !== 'browse'
  ) {
    return {
      section: {
        type: 'stream',
        sortChannel: sortChannel ?? '',
        sectionIndexOnPage: 1,
        recoType,
        linkedContent,
      } as TrackingSectionContext,
      element: {
        type: 'element-context',
        rowIndexInSection: 1,
        columnIndexInSection: 1,
        elementIndexInSection: 1,
        productIndexOnPage: 1,
      } as TrackingElementContext,
    };
  } else {
    return {
      section: {
        type: elements !== undefined ? 'reco' : 'simple',
        recoType,
        blockTypeAndId,
        sectionIndexOnPage: sectionIndex ?? 1,
        initiallyVisibleColumns: 1,
        elements: elements || [{id: NaN, type: 'product'}], // fall back
        headline,
        linkedContent,
      } as TrackingRecoSectionContext,
      element: {
        type: 'element-context',
        rowIndexInSection: 1,
        columnIndexInSection: 1,
        elementIndexInSection: 1,
        productIndexOnPage: 1,
      } as TrackingElementContext,
    };
  }
};
